import { t } from '@avalara/bat-apps-shared/i18n';
import { SCardAction } from '@avalara/skylab-sdk/react';
import { useHistory, useLocation } from 'react-router-dom';

import conf from '../../conf';
import { openExternalLink } from '../../lib/helpers';
import { getResolvedUrl } from '../../lib/product-catalog';
import './product-catalog.css';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { ProductCatalogItem } from '../../../../types/product-catalog';

type Props = {
  item: ProductCatalogItem;
  hasAccess: boolean;
  onContactClick: (item: ProductCatalogItem) => void;
};

const CatalogCard = ({ item, hasAccess, onContactClick }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const learnMoreUrl = getResolvedUrl(item.learnMoreLink);
  const openUrl = getResolvedUrl(item.openLink);

  const handlePrimaryClick = () => {
    if (hasAccess) {
      if (openUrl) openExternalLink(openUrl);
      return;
    }

    onContactClick(item);
  };

  const handleLearnMoreClick = () => {
    if (item.hasProductDetailPage) {
      // Forward any query params from the catalog page itself (e.g. ?embedded=true, non-prod
      // feature-flag overrides) so they still apply once the customer lands on the PDP.
      history.push(`/product-catalog/${item.slug}${location.search}`);
      return;
    }

    if (learnMoreUrl) openExternalLink(learnMoreUrl);
  };

  return (
    <SCardAction className='product-catalog-card' data-ref={`buy.product-catalog.${item.slug}.card`}>
      <div slot='title' className='product-catalog-card-title-slot'>
        <div className='product-catalog-card-title-row'>
          <img
            src={`${conf.ASSET_PATH}/assets/images/product-catalog/${item.image}`}
            slot='thumbnail'
            alt=''
            width={32}
            height={32}
          />
          <h3 className='pad-left-md'>{item.title}</h3>
        </div>
        {hasAccess && (
          <span
            className='product-catalog-card-purchased-badge'
            data-ref={`buy.product-catalog.${item.slug}.purchased-badge`}
          >
            {t('client.product-catalog.badge.purchased')}
          </span>
        )}
      </div>
      <div slot='description' className='margin-top-none product-catalog-card-description'>
        <p>{t(item.description as TranslationKey)}</p>
      </div>
      <div slot='footer' className='product-catalog-card-footer'>
        <button
          type='button'
          className='secondary margin-right-sm'
          data-ref={`buy.product-catalog.${item.slug}.primary-button`}
          onClick={handlePrimaryClick}
          disabled={hasAccess && !openUrl}
        >
          {t(hasAccess ? 'client.buttons.open' : 'client.buttons.contact-us')}
        </button>
        {!hasAccess && (
          <button
            type='button'
            className='ghost-blue'
            data-ref={`buy.product-catalog.${item.slug}.learn-more-button`}
            onClick={handleLearnMoreClick}
            disabled={!item.hasProductDetailPage && !learnMoreUrl}
          >
            {t('client.general.learn-more')}
          </button>
        )}
      </div>
    </SCardAction>
  );
};

export default CatalogCard;
